import React, { useState } from "react";
import DashboardMainContainer from "../common/Containers/DashboardMainContainer";
import {
  AdminMainText,
  AdminMetadataText,
  AdminSupportingText,
  AdminTableBody,
} from '../../sections/AdminDashboard/shared/adminUx';
import { AdminWidgetHeader, AdminWidgetMenuFilter } from './AdminWidgetChrome';

const ActivityFeedWidget = ({
  className = 'masonry-item w-[440px] h-[713px] flex flex-col',
}) => {
  const [sortOrder, setSortOrder] = useState('newest');
  const [readActivities, setReadActivities] = useState(new Set());

  // Sample activity data with timestamps for sorting
  const activitiesData = {
    today: [
      { id: 1, message: "John Doe sent you a message", time: "2 min ago", user: "John Doe", timestamp: Date.now() - 2 * 60 * 1000 },
      { id: 2, message: "New review received", time: "1 hour ago", timestamp: Date.now() - 60 * 60 * 1000 },
      { id: 3, message: "Post published", time: "3 hour ago", timestamp: Date.now() - 3 * 60 * 60 * 1000 },
      { id: 4, message: "Post refreshed", time: "Today, 8:03 AM", timestamp: Date.now() - 10 * 60 * 60 * 1000 },
      { id: 9, message: "New follower added", time: "Today, 7:50 AM", timestamp: Date.now() - 11 * 60 * 60 * 1000 },
    ],
    yesterday: [
      { id: 5, message: "Profile updated", time: "Yesterday, 5:33 PM", timestamp: Date.now() - 30 * 60 * 60 * 1000 },
      { id: 17, message: "Billing info verified", time: "Yesterday, 4:18 PM", timestamp: Date.now() - 31 * 60 * 60 * 1000 },
    ],
    older: [
      { id: 6, message: "Post Deleted", time: "Sunday, 11:23 AM", timestamp: Date.now() - 4 * 24 * 60 * 60 * 1000 },
      { id: 7, message: "Subscription renewed", time: "July 18, 2025", timestamp: Date.now() - 30 * 24 * 60 * 60 * 1000 },
      { id: 8, message: "John Doe sent you a message", time: "Jan 23, 2025", user: "John Doe", timestamp: Date.now() - 90 * 24 * 60 * 60 * 1000 },
      { id: 22, message: "Profile completed 80%", time: "Dec 10, 2024", timestamp: Date.now() - 120 * 24 * 60 * 60 * 1000 }
    ]
  };

  // Sort activities based on sortOrder
  const getSortedActivities = () => {
    const allActivities = [
      ...activitiesData.today,
      ...activitiesData.yesterday,
      ...activitiesData.older
    ];

    const sorted = [...allActivities].sort((a, b) => {
      if (sortOrder === 'newest') {
        return b.timestamp - a.timestamp; // Newest first
      } else {
        return a.timestamp - b.timestamp; // Oldest first
      }
    });

    // Group back into categories
    const now = Date.now();
    const oneDayAgo = now - 24 * 60 * 60 * 1000;
    const twoDaysAgo = now - 2 * 24 * 60 * 60 * 1000;

    return {
      today: sorted.filter(a => a.timestamp >= oneDayAgo),
      yesterday: sorted.filter(a => a.timestamp < oneDayAgo && a.timestamp >= twoDaysAgo),
      older: sorted.filter(a => a.timestamp < twoDaysAgo)
    };
  };

  const activities = getSortedActivities();

  const handleSortChange = (value) => {
    setSortOrder(value);
  };

  const handleMarkAllRead = () => {
    const allActivityIds = [
      ...activities.today,
      ...activities.yesterday,
      ...activities.older
    ].map(activity => activity.id);
    setReadActivities(new Set(allActivityIds));
  };

  const renderActivity = (activity) => (
    <div key={activity.id} className="flex items-start gap-3 py-2">
      {!readActivities.has(activity.id) && (
        <div className="w-2 h-2 bg-mediumgray rounded-full mt-2 flex-shrink-0"></div>
      )}
      {readActivities.has(activity.id) && (
        <div className="w-0 h-2 mt-2 flex-shrink-0"></div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <AdminMainText className="text-offblack flex-1 min-w-0">
            {activity.user ? (
              <span>
                <AdminTableBody emphasis className="text-offblack">
                  {activity.user}
                </AdminTableBody>
                {activity.message.replace(activity.user, '')}
              </span>
            ) : (
              activity.message
            )}
          </AdminMainText>
          <AdminMetadataText className="text-offblack ml-2 flex-shrink-0">
            {activity.time}
          </AdminMetadataText>
        </div>
      </div>
    </div>
  );

  const sortLabel = sortOrder === 'newest' ? 'Newest First' : 'Oldest First';

  return (
    <DashboardMainContainer className={className}>
      <AdminWidgetHeader title="Activity Feed">
        <AdminWidgetMenuFilter
          label={sortLabel}
          items={[
            { key: 'newest', label: 'Newest First', active: sortOrder === 'newest', onClick: () => handleSortChange('newest') },
            { key: 'oldest', label: 'Oldest First', active: sortOrder === 'oldest', onClick: () => handleSortChange('oldest') },
            { key: 'read', label: 'Mark All Read', onClick: handleMarkAllRead },
          ]}
        />
      </AdminWidgetHeader>
      
      <div className="flex-1 p-4 space-y-6 overflow-y-auto">
        {sortOrder === 'newest' ? (
          <>
            {/* Today */}
            {activities.today.length > 0 && (
              <div>
                <AdminSupportingText className="text-offblack mb-3 block">Today</AdminSupportingText>
                <div className="space-y-1">
                  {activities.today.map(renderActivity)}
                </div>
              </div>
            )}

            {/* Yesterday */}
            {activities.yesterday.length > 0 && (
              <div>
                <AdminSupportingText className="text-offblack mb-3 block">Yesterday</AdminSupportingText>
                <div className="space-y-1">
                  {activities.yesterday.map(renderActivity)}
                </div>
              </div>
            )}

            {/* Older */}
            {activities.older.length > 0 && (
              <div>
                <AdminSupportingText className="text-offblack mb-3 block">Older</AdminSupportingText>
                <div className="space-y-1">
                  {activities.older.map(renderActivity)}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Older */}
            {activities.older.length > 0 && (
              <div>
                <AdminSupportingText className="text-offblack mb-3 block">Older</AdminSupportingText>
                <div className="space-y-1">
                  {activities.older.map(renderActivity)}
                </div>
              </div>
            )}

            {/* Yesterday */}
            {activities.yesterday.length > 0 && (
              <div>
                <AdminSupportingText className="text-offblack mb-3 block">Yesterday</AdminSupportingText>
                <div className="space-y-1">
                  {activities.yesterday.map(renderActivity)}
                </div>
              </div>
            )}

            {/* Today */}
            {activities.today.length > 0 && (
              <div>
                <AdminSupportingText className="text-offblack mb-3 block">Today</AdminSupportingText>
                <div className="space-y-1">
                  {activities.today.map(renderActivity)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardMainContainer>
  );
};

export default ActivityFeedWidget;
